import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { ArrowLeft, Calendar, User, Tag, Newspaper, Clock, Loader2 } from 'lucide-react';
import { listNews, DEFAULT_BASE_URL } from '../services/api';
import { getText, getCurrentLanguage } from '../services/dataSync';

const NewsDetailPage = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  const getApiOrigin = () => {
    try {
      const apiBase = DEFAULT_BASE_URL.replace(/\/$/, '');
      const parsed = new URL(apiBase);
      return `${parsed.protocol}//${parsed.host}`;
    } catch {
      return window.location.origin;
    }
  };

  const getNewsImageSrc = (news) => {
    const raw = news?.imageUrl || news?.image || '';
    if (!raw) return '';
    if (raw.startsWith('http') || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
    if (raw.startsWith('/')) return `${getApiOrigin()}${raw}`;
    return raw;
  };

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  // Get category color function
  const getCategoryColor = (category) => {
    const colors = {
      'Announcement': 'bg-blue-100 text-blue-800 border-blue-200',
      'Event': 'bg-purple-100 text-purple-800 border-purple-200',
      'Update': 'bg-green-100 text-green-800 border-green-200',
      'Policy': 'bg-amber-100 text-amber-800 border-amber-200',
      'Industry': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'General': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  useEffect(() => {
    const load = async () => {
      try {
        const all = await listNews();
        // Sort by date in descending order (newest first)
        const sorted = Array.isArray(all)
          ? all.sort((a, b) => {
              const dateA = new Date(a.publishedAt || a.createdAt || a.date || 0);
              const dateB = new Date(b.publishedAt || b.createdAt || b.date || 0);
              return dateB - dateA; // Descending order
            })
          : [];
        const found = sorted.find(n => (n._id || n.id) === id);
        setItem(found || null);
        // Get recent 5 news (excluding current item), already sorted
        const recent5 = sorted
          .filter(n => (n._id || n.id) !== id)
          .slice(0, 5);
        setRecent(recent5);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
              <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
            </div>
            <p className="text-gray-600 font-medium">{getText('news.detail.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{getText('news.detail.notFound')}</h1>
            <p className="text-gray-600 mb-6">{getText('news.detail.notFoundDesc')}</p>
            <Link 
              to="/news" 
              className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-all shadow-md hover:shadow-lg"
            >
              <ArrowLeft className="h-4 w-4" />
              {getText('news.detail.backToNews')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Format date based on current language
  const formatDate = (date) => {
    if (!date) return getText('news.detail.recently');
    
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return getText('news.detail.recently');
      
      const localeMap = {
        'en': 'en-US',
        'hi': 'hi-IN',
        'mr': 'mr-IN'
      };
      const locale = localeMap[currentLang] || 'en-US';
      
      return dateObj.toLocaleDateString(locale, { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return getText('news.detail.recently');
    }
  };

  const publishedDate = item.publishedAt || item.createdAt || item.date;
  const formattedDate = formatDate(publishedDate);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Header Section */}
      <section className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="mb-4">
            <Link 
              to="/news" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-lg transition-all duration-300 group shadow-md hover:shadow-lg"
            >
              <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium text-sm">{getText('news.detail.backToNews')}</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {/* Main Article */}
            <article className="md:col-span-2 lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                {/* Article Header */}
                <div className="p-6 md:p-8 border-b border-gray-200">
                  {item.category && (
                    <div className="mb-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${getCategoryColor(item.category)}`}>
                        <Tag className="h-3 w-3" />
                        {item.category}
                      </span>
                    </div>
                  )}
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                    {item.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                    {item.author && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span className="font-medium">{item.author}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{formattedDate}</span>
                    </div>
                    {item.isBreaking && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold">
                        <Clock className="h-3 w-3 animate-pulse" />
                        {getText('news.detail.breakingNews')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Article Image */}
                {getNewsImageSrc(item) && (
                  <div className="w-full overflow-hidden">
                    <img 
                      src={getNewsImageSrc(item)} 
                      alt={item.title} 
                      className="w-full h-auto object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {/* Article Content */}
                <div className="p-6 md:p-8">
                  {item.summary && (
                    <div 
                      className="prose prose-lg max-w-none mb-8 text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.summary) }} 
                    />
                  )}
                  {item.content && (
                    <div 
                      className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content) }} 
                    />
                  )}
                  {!item.summary && !item.content && (
                    <p className="text-gray-600 italic">{getText('news.detail.noContent')}</p>
                  )}
                </div>
              </div>
            </article>

            {/* Sidebar - Recent News */}
            <aside className="md:col-span-1 lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6 sticky top-24">
                <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
                  <Newspaper className="h-5 w-5 text-primary-600" />
                  <h2 className="text-lg font-bold text-gray-900">{getText('news.detail.recentNews')}</h2>
                </div>
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-6 w-6 text-gray-400 animate-spin mx-auto" />
                  </div>
                ) : recent.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    {getText('news.detail.noRecentNews')}
                  </div>
                ) : (
                  <ul className="space-y-4">
                    {recent.map((n, index) => (
                      <li key={n._id || n.id} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                        <div className="mt-2 h-2 w-2 rounded-full bg-primary-600 flex-shrink-0"></div>
                        <Link 
                          to={`/news/${n._id || n.id}`} 
                          className="text-sm text-gray-800 hover:text-primary-600 transition-colors line-clamp-2 flex-1 font-medium leading-relaxed"
                        >
                          {n.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsDetailPage;
